import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * One answer for every screen (D1/D2 plan step 3): GET /api/platform/experience says, for the selected organisation
 * and the caller's roles there, which menu items to draw, where to land, which mobile tabs to show and which screens
 * belong to modules that are off. Nothing is drawn until it arrives (fails closed); if it can't be loaded, only the
 * always-on settings screens are drawn. The server still enforces access on every request.
 */
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Always-on screens and tabs: what is drawn when the answer can't be loaded at all.
export const KERNEL_SCREENS = ['nav-settings-dashboard', 'nav-settings-security', 'nav-settings-ai', 'nav-settings-team',
  'nav-settings-access'];
export const KERNEL_TABS = ['home', 'more', 'notifications', 'security'];

const EMPTY = Object.freeze({ status: 'anonymous', nav: new Set(), offScreens: new Set(), mobileTabs: new Set(),
  landing: null, keys: {}, api: {}, actions: {}, departmentGrants: {}, roles: [], organizationId: null, licence: null });

function shape(body) {
  return {
    status: 'ready',
    nav: new Set(body.nav || []),
    offScreens: new Set(body.off_screens || []),
    mobileTabs: new Set(body.mobile_tabs || []),
    landing: body.landing || null,
    keys: body.keys || {},
    api: body.api || {},
    actions: body.actions || {},
    departmentGrants: body.department_grants || {},
    roles: body.roles || [],
    organizationId: body.organization_id || null,
    // The licence from the vendor's office ({state: ok|grace|expired, valid_until, grace_until, core_only}).
    licence: body.licence || null,
  };
}

const failed = () => ({ ...EMPTY, status: 'error', nav: new Set(KERNEL_SCREENS), mobileTabs: new Set(KERNEL_TABS) });

/** Fetch the experience for the selected organisation (the one the org switcher stored). */
export async function fetchExperience(token, { organizationId, locationId, department, signal } = {}) {
  const orgId = organizationId ?? localStorage.getItem('current_organization_id');
  const loc = locationId ?? localStorage.getItem('current_location_id');
  const dept = department ?? localStorage.getItem('active_department');
  const headers = { Authorization: `Bearer ${token}` };
  if (orgId) headers['X-Organization-Id'] = orgId;
  if (loc) headers['X-Location-Id'] = loc;
  if (dept) headers['X-Department'] = dept;
  const query = loc ? `?location_id=${encodeURIComponent(loc)}` : '';
  const res = await fetch(`${API_URL}/api/platform/experience${query}`, { headers, signal });
  if (!res.ok) throw new Error(`experience ${res.status}`);
  return shape(await res.json());
}

/** The experience for a token: 'loading' until it arrives, 'error' (kernel screens only) if it can't be loaded. */
export function useExperienceFor(token) {
  const [state, setState] = useState(() => (token ? { ...EMPTY, status: 'loading' } : EMPTY));
  const [scopeTick, setScopeTick] = useState(0);
  useEffect(() => {
    const bump = () => setScopeTick((n) => n + 1);
    const storageChanged = (e) => {
      if (!e.key || ['active_department', 'current_organization_id', 'current_location_id'].includes(e.key)) bump();
    };
    window.addEventListener('fi-department-changed', bump);
    window.addEventListener('storage', storageChanged);
    window.addEventListener('locationChanged', bump);
    window.addEventListener('organizationChanged', bump);
    return () => {
      window.removeEventListener('fi-department-changed', bump);
      window.removeEventListener('storage', storageChanged);
      window.removeEventListener('locationChanged', bump);
      window.removeEventListener('organizationChanged', bump);
    };
  }, []);
  useEffect(() => {
    if (!token) { setState(EMPTY); return undefined; }
    const controller = new AbortController();
    setState((s) => ({ ...s, status: 'loading' }));
    fetchExperience(token, { signal: controller.signal })
      .then((exp) => { if (!controller.signal.aborted) setState(exp); })
      .catch((e) => { if (!controller.signal.aborted && e?.name !== 'AbortError') setState(failed()); });
    return () => controller.abort();
  }, [token, scopeTick]);
  return state;
}

const ExperienceContext = createContext(EMPTY);

export function ExperienceProvider({ token, children }) {
  const value = useExperienceFor(token);
  return <ExperienceContext.Provider value={value}>{children}</ExperienceContext.Provider>;
}

export const useExperience = () => useContext(ExperienceContext);

// The roles the server lets decide something, as its routes declare them (require_role). A screen draws the button
// only for these, so nobody is offered an action that ends in "Insufficient permissions".
export const INVOICE_DECIDERS = ['org_owner', 'org_admin', 'manager'];       // pipeline_api.py: approve, hold, ...
export const SUPPLIER_APPROVERS = ['org_owner', 'org_admin'];                 // vendor_onboarding.APPROVER_ROLES
export const VENDOR_WRITERS = ['org_owner', 'org_admin', 'manager'];          // vendors_api.py: create, edit, link, compliance
export const STOCK_MOVERS = ['org_owner', 'org_admin', 'manager'];            // inventory_api.py: POST /movements
export const ALERT_OPERATORS = ['org_owner', 'org_admin', 'manager', 'staff']; // alerts_api.py: POST /alerts/{id}/acknowledge
export const ALERT_MANAGERS = ['org_owner', 'org_admin', 'manager'];          // alerts_api.py: resolve, snooze, escalate
export const OPERATE_ROLES = ['org_owner', 'org_admin', 'manager', 'staff'];  // pipeline_api.py / receiving/api.py
export const SETUP_ROLES = ['org_owner', 'org_admin', 'manager'];             // onboarding/api.py: status, item details
export const COMPLIANCE_ROLES = ['org_owner', 'org_admin', 'manager'];        // compliance/api.py: profile, register, records

export const ACTION_ROLES = Object.freeze({
  'invoices.upload': OPERATE_ROLES,
  'invoices.decide': INVOICE_DECIDERS,
  'vendors.write': VENDOR_WRITERS,
  'vendors.approve': SUPPLIER_APPROVERS,
  'receiving.operate': OPERATE_ROLES,
  'receiving.capture': OPERATE_ROLES,
  'receiving.confirm': INVOICE_DECIDERS,
  'ck.operate': OPERATE_ROLES,
  'ck.manage': INVOICE_DECIDERS,
  'driver.receive': ['driver'],
  'waste.record': OPERATE_ROLES,
  'waste.decide': INVOICE_DECIDERS,
  'scan_gates.manage': INVOICE_DECIDERS,
  'inventory.move': STOCK_MOVERS,
  'inventory.reorder': STOCK_MOVERS,
  'inventory.count': STOCK_MOVERS,
  'alerts.acknowledge': ALERT_OPERATORS,
  'alerts.resolve': ALERT_MANAGERS,
  'setup.manage': SETUP_ROLES,
  'compliance.manage': COMPLIANCE_ROLES,
  'team.manage': SUPPLIER_APPROVERS,
});

/** Whether the signed-in person holds one of `allowed`. Until the experience has loaded, or when it failed, the
 * answer is yes: the server still refuses, and a manager never loses a button to a slow request. */
export function mayDo(exp, allowed) {
  if (!exp || exp.status !== 'ready') return true;
  return (exp.roles || []).some((r) => allowed.includes(r));
}

/** Whether the server allows this action (`GET /api/platform/experience` `actions[key]`), falling back to
 * `ACTION_ROLES[key]` when a test stub passes `roles` without `actions`. Stays open while loading. */
export function canDo(exp, actionKey) {
  if (!exp || exp.status !== 'ready') return true;
  if (exp.actions && typeof exp.actions[actionKey] === 'boolean') return exp.actions[actionKey];
  const allowed = ACTION_ROLES[actionKey];
  return allowed ? mayDo(exp, allowed) : true;
}

/** Which menu item a path opens: the longest menu path that is the path or a parent of it. */
export function screenForPath(pathname, menuItems) {
  let best = null;
  for (const it of menuItems) {
    if (!it.to || !it.testid) continue;
    if (pathname === it.to || pathname.startsWith(`${it.to}/`)) {
      if (!best || it.to.length > best.to.length) best = it;
    }
  }
  return best ? best.testid : null;
}

/**
 * Whether the server lets this organisation reach an API path: the longest catalogue prefix that is the path or a
 * parent of it decides. Only a loaded answer can say yes, so a probe waits for it and an unreadable answer probes
 * nothing; a path the catalogue does not list is not blocked here (the server still decides).
 */
export function apiAllowed(exp, path) {
  if (!exp || exp.status !== 'ready') return false;
  let best = null;
  for (const prefix of Object.keys(exp.api || {})) {
    if ((path === prefix || path.startsWith(`${prefix}/`)) && (best === null || prefix.length > best.length)) best = prefix;
  }
  return best === null ? true : exp.api[best] === true;
}

/**
 * Where a signed-in person starts: their persona's landing if the server gave one; else `preferred` unless its screen
 * is not on their menu (a deployment without that module); then the first menu item they do have.
 */
export function landingFor(exp, menuItems, preferred) {
  if (exp.landing) return exp.landing;
  const screen = screenForPath(preferred, menuItems);
  if (!screen || exp.nav.has(screen)) return preferred;
  const first = menuItems.find((it) => it.to && exp.nav.has(it.testid));
  return first ? first.to : preferred;
}

/**
 * Refuses a screen whose module is off for this organisation, so a hidden screen is not one URL away. Screens a
 * persona menu only leaves out stay reachable by link. Paths that are not menu screens are not guarded here.
 */
export function ExperienceGuard({ menuItems, signedIn, children }) {
  const exp = useExperience();
  const { pathname } = useLocation();
  const screen = useMemo(() => screenForPath(pathname, menuItems), [pathname, menuItems]);
  if (!signedIn || !screen) return children;
  if (exp.status === 'loading') {
    return <div className="max-w-3xl mx-auto px-4 py-12 text-ink-muted" data-testid="experience-loading">Loading…</div>;
  }
  if (exp.status === 'error' && !exp.nav.has(screen)) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-12" data-testid="experience-error">
        <h1 className="font-display text-2xl text-ink">This screen could not be checked</h1>
        <p className="text-ink-muted mt-2">Your menu could not be loaded, so this screen is not shown. Reload the page to try again.</p>
      </main>
    );
  }
  if (exp.offScreens.has(screen)) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-12" data-testid="module-not-enabled">
        <h1 className="font-display text-2xl text-ink">Not enabled for your organisation</h1>
        <p className="text-ink-muted mt-2">This part of the product is not switched on here. An owner or admin can ask to have it added.</p>
      </main>
    );
  }
  return children;
}
